import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { CommunicationSignatureFilters } from '@client/components/communication-signature/CommunicationSignatureFilters';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Tag } from 'antd';
import { useState } from 'react';

export const Route = createFileRoute('/console/$orgId/configuration/org-settings/communication/comm-signatures')({
    staticData: {
        metadata: {
            title: 'Communication signatures',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <CommSignatureSearch />
        </PageController>
    );
}

type QueryParams = RouterInputs['commSignature']['listCommSignatures'];

const columnHelper = createColumnHelper<
    RouterOutputs['commSignature']['listCommSignatures']['rows'][number],
    QueryParams['orderBy']
>();

function CommSignatureSearch() {
    const orgId = useOrgId();
    const { trpcUtils } = useTrpc();
    const { mutate } = trpc.commSignature.deleteCommSignature.useMutation();
    const navigate = useNavigate();

    const [input, setInput] = useState<QueryParams>({
        orderBy: { id: 'desc' },
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
    });

    const { data, isPending, error, refetch, isFetching } = trpc.commSignature.listCommSignatures.useQuery(input);

    const tableProps = usePrismaTable({
        data: data?.rows,
        total: data?.total,
        loading: isPending,
        rowKey: 'id',
        queryParams: input,
        onParamsChange: setInput,
        emptyState: { noRecordsTitle: 'No communication signatures yet' },
        columns: [
            columnHelper.accessor('id', {
                id: 'id',
                header: 'ID',
                sortable: 'id',
            }),
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Name',
                sortable: 'name',
            }),
            columnHelper.accessor('isDefault', {
                id: 'isDefault',
                header: 'Is default',
                sortable: 'isDefault',
                cell: ({ row }) =>
                    row.isDefault ? (
                        <Tag color="success" variant="outlined">
                            Yes
                        </Tag>
                    ) : (
                        <Tag color="default" variant="outlined">
                            No
                        </Tag>
                    ),
            }),
            columnHelper.actions({
                cell: ({ row }) => (
                    <Button.Delete
                        onConfirm={(callback) => {
                            mutate(
                                { id: row.id },
                                {
                                    onSuccess() {
                                        callback();
                                        void trpcUtils.commSignature.invalidate();
                                    },
                                    onError(error) {
                                        errorMessage.show(error);
                                    },
                                },
                            );
                        }}
                    />
                ),
            }),
        ],
    });

    if (error) {
        return <InlineQueryError error={error} onRetry={() => void refetch()} retrying={isFetching} />;
    }

    return (
        <>
            <Outlet />

            <TableToolbar
                filters={
                    <CommunicationSignatureFilters
                        urlKey="communication-signature-search-filters"
                        onChange={(where) => setInput((prev) => ({ ...prev, where, offset: 0 }))}
                    />
                }
                actions={
                    <Button.Add.Link
                        to="/console/$orgId/configuration/org-settings/communication/comm-signatures/add"
                        params={{ orgId }}
                        variant="primary"
                        label="New communication signature"
                    />
                }
            />

            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/configuration/org-settings/communication/comm-signatures/edit/$commSignatureId',
                                params: { orgId, commSignatureId: id },
                            },
                            e,
                        );
                    },
                })}
            />
        </>
    );
}
